import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGlobalSearch, type SearchResultItem } from "../../api/client";
import { Icon, type IconName } from "../icons/Icon";
import { EmptyState, Skeleton } from "../ui/feedback";
import "../../styles/components/layout.css";

const TYPE_META: Record<string, { label: string; icon: IconName }> = {
  material: { label: "物料", icon: "inbox" },
  sales_order: { label: "销售订单", icon: "shop" },
  work_order: { label: "工单", icon: "schedule" },
  customer: { label: "客户", icon: "team" },
  employee: { label: "员工", icon: "idcard" },
};

/**
 * 全局搜索（Ctrl/⌘+K）：跨物料/订单/工单/客户统一入口。
 * 触发方式：快捷键，或顶栏搜索按钮派发的 `open-global-search` 事件。
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-global-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-global-search", onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 20);
    else {
      setKeyword("");
      setItems([]);
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    const kw = keyword.trim();
    if (!open || kw.length < 2) {
      setItems([]);
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await apiGlobalSearch(kw, 20);
        if (alive) setItems(result ?? []);
      } catch (err) {
        console.error("[global-search] failed", err);
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [keyword, open]);

  if (!open) return null;

  const select = (item: SearchResultItem) => {
    setOpen(false);
    navigate(item.url);
  };

  return (
    <div className="gsearch-mask" onClick={() => setOpen(false)}>
      <div className="gsearch" role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="gsearch-input-row">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            className="gsearch-input"
            placeholder="搜索物料编码、订单号、工单号、客户或员工（至少 2 个字符）"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((i) => Math.min(items.length - 1, i + 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((i) => Math.max(0, i - 1));
              } else if (e.key === "Enter") {
                const item = items[activeIndex];
                if (item) {
                  e.preventDefault();
                  select(item);
                }
              }
            }}
          />
          <kbd className="gsearch-kbd">Esc</kbd>
        </div>

        <div className="gsearch-body">
          {loading ? (
            <div className="gsearch-loading">
              <Skeleton width="60%" height={14} />
              <Skeleton width="80%" height={14} />
              <Skeleton width="45%" height={14} />
            </div>
          ) : items.length > 0 ? (
            items.map((item, index) => {
              const meta = TYPE_META[item.type] ?? { label: item.type, icon: "search" as IconName };
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  type="button"
                  className={`gsearch-item${index === activeIndex ? " is-active" : ""}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => select(item)}
                >
                  <span className="gsearch-item-icon">
                    <Icon name={meta.icon} size={15} />
                  </span>
                  <span className="gsearch-item-main">
                    <span className="gsearch-item-name">{item.name}</span>
                    <span className="gsearch-item-sub">{item.sub}</span>
                  </span>
                  <span className="gsearch-item-type">{meta.label}</span>
                </button>
              );
            })
          ) : keyword.trim().length >= 2 ? (
            <EmptyState compact icon="search" title="没有找到匹配结果" description="试试更短的关键字，或用物料编码前缀搜索。" />
          ) : (
            <div className="gsearch-tip">
              <div className="gsearch-tip-title">支持的搜索范围</div>
              <div className="gsearch-tip-list">
                {Object.entries(TYPE_META).map(([key, meta]) => (
                  <span className="gsearch-tip-item" key={key}>
                    <Icon name={meta.icon} size={13} />
                    {meta.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
